import { Mail, UserCheck, UserPlus } from "lucide-react";
import { useState } from "react";
import { PortalInvitationDialog } from "./PortalInvitationDialog";

interface PortalUser {
  id: string;
  name: string;
  email: string;
}

interface PortalInvitation {
  id: string;
  email: string;
  name: string | null;
  expiresAt: string;
  accepted: boolean;
}

interface PortalAccessPanelProps {
  campaignId: string;
  portalUsers: PortalUser[];
  portalInvitations: PortalInvitation[];
  onRefresh: () => void;
}

export function PortalAccessPanel({
  campaignId,
  portalUsers,
  portalInvitations,
  onRefresh,
}: PortalAccessPanelProps) {
  const [showInviteForm, setShowInviteForm] = useState(false);

  const pendingInvitations = portalInvitations.filter((i) => !i.accepted);

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          Portal Access
        </h3>
        <button
          type="button"
          onClick={() => setShowInviteForm(true)}
          className="flex items-center gap-1 rounded bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent/90"
        >
          <UserPlus size={12} />
          Invite
        </button>
      </div>

      {showInviteForm && (
        <div className="mt-3">
          <PortalInvitationDialog
            campaignId={campaignId}
            onDone={() => {
              setShowInviteForm(false);
              onRefresh();
            }}
            onCancel={() => setShowInviteForm(false)}
          />
        </div>
      )}

      <div className="mt-3 space-y-2">
        {portalUsers.map((user) => (
          <div key={user.id} className="flex items-center gap-2 text-sm">
            <UserCheck size={14} className="text-green-500" />
            <span className="text-text-secondary">{user.name}</span>
            <span className="text-text-faint">{user.email}</span>
          </div>
        ))}

        {pendingInvitations.map((inv) => (
          <div key={inv.id} className="flex items-center gap-2 text-sm">
            <Mail size={14} className="text-amber-500" />
            <span className="text-text-secondary">{inv.name || inv.email}</span>
            {inv.name && <span className="text-text-faint">{inv.email}</span>}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              Pending
            </span>
          </div>
        ))}

        {portalUsers.length === 0 && pendingInvitations.length === 0 && !showInviteForm && (
          <p className="text-xs text-text-faint">No portal users invited yet.</p>
        )}
      </div>
    </div>
  );
}
